import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Modal, Card, Descriptions, Typography, Spin, Input } from 'antd'
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api'

const { Text } = Typography

const containerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.15)'
}

const mapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
}

const defaultCenter = { lat: 37.7749, lng: -122.4194 }

const LocationPickerModal = ({ open, onClose, onLocationSelect, value }) => {
  const [mapCenter, setMapCenter] = useState(defaultCenter)
  const [selected, setSelected] = useState(null)
  const [address, setAddress] = useState(null)
  const [loadingAddress, setLoadingAddress] = useState(false)
  const [searchText, setSearchText] = useState('')
  const searchInputRef = useRef(null)
  const mapRef = useRef(null)

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places']
  })

  const getAddressFromLatLng = function (lat, lng) {
    setLoadingAddress(true)
    const geocoder = new window.google.maps.Geocoder()
    geocoder.geocode({ location: { lat: lat, lng: lng } }, function (
      results,
      status
    ) {
      if (status === 'OK' && results && results.length > 0) {
        const components = results[0].address_components
        function getComponent(types) {
          for (let i = 0; i < components.length; i++) {
            var matched = types.every(t => components[i].types.includes(t))
            if (matched) return components[i].long_name
          }
          return null
        }

        results[0].formatted_address && setAddress({
          town: getComponent(['sublocality']) || getComponent(['neighborhood']),
          full: results[0].formatted_address
        })
      }
      else {
        setAddress(null)
      }
      setLoadingAddress(false)
    })
  }

  const onMapClick = useCallback(function (event) {
    const coords = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    }
    setSelected(coords)
    getAddressFromLatLng(coords.lat, coords.lng)
  }, [])

  const handleConfirm = function () {
    if (selected) {
      onLocationSelect(Object.assign({}, selected, address || {}))
    }
  }
  useEffect(() => {
    if (open) {
      // Sync/Reset search input
      setSearchText((value && value.full) || '')

      if (value && value.lat && value.lng) {
        setMapCenter({ lat: value.lat, lng: value.lng })
        setSelected(value)
        // If we have coords but no address info, fetch it
        if (!value.full) {
          getAddressFromLatLng(value.lat, value.lng)
        } else {
          setAddress({ full: value.full })
        }
      } else {
        setSelected(null)
        setAddress(null)
        // Try to get user's current location
        navigator.geolocation.getCurrentPosition(
          pos => {
            const coords = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude
            }
            setMapCenter(coords)
          },
          () => {
            setMapCenter(defaultCenter) // fallback
          }
        )
      }
    }
  }, [open, value, isLoaded])

  const onLoadMap = map => {
    mapRef.current = map

    if (window.google && searchInputRef.current) {
      const input = searchInputRef.current.input
      const autocomplete = new window.google.maps.places.Autocomplete(input)
      autocomplete.bindTo('bounds', map)

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()
        if (!place.geometry || !place.geometry.location) return

        const lat = place.geometry.location.lat()
        const lng = place.geometry.location.lng()
        const coords = { lat, lng }
        setSelected(coords)
        if (place.formatted_address) {
          setAddress({
            town: place.name || place.formatted_address,
            full: place.formatted_address
          })
          setSearchText(place.formatted_address)
        }
        getAddressFromLatLng(lat, lng)
        map.panTo(coords)
      })
    }
  }

  return (
    <Modal
      title="📍 Select Location"
      visible={open}
      onCancel={onClose}
      onOk={handleConfirm}
      okText="Confirm"
      width={700}
      okButtonProps={{ disabled: !selected }}
    >
      {isLoaded ? (
        <>
          <Input
            placeholder="Search location..."
            ref={searchInputRef}
            style={{ marginBottom: 12 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter}
            zoom={13}
            options={mapOptions}
            onClick={onMapClick}
            onLoad={onLoadMap}
          >
            {selected ? <Marker position={selected} /> : null}
          </GoogleMap>

          {selected ? (
            <Card
              title="Selected Location Info"
              style={{ marginTop: 16 }}
              bordered
              type="inner"
            >
              {loadingAddress ? (
                <Spin />
              ) : (
                <Descriptions column={1}>
                  <Descriptions.Item label="Latitude">
                    {selected.lat.toFixed(5)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Longitude">
                    {selected.lng.toFixed(5)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Town">
                    {(address && address.town) || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Full Address">
                    <Text type="secondary">
                      {(address && address.full) || 'N/A'}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>
              )}
            </Card>
          ) : null}
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <Spin tip="Loading Map..." />
        </div>
      )}
    </Modal>
  )
}

export default LocationPickerModal
